import { useMutation } from "@tanstack/react-query"

import { useToast } from "@/components/ui/use-toast"
import { useScopedI18n } from "@/locales/client"
import {
  addItemToOrderMutation,
  AddItemToOrderMutationVariables,
  adjustItemQuantityMutation,
  AdjustItemQuantityMutationVariables,
  applyCouponCodeMutation,
  ApplyCouponCodeMutationVariables,
  removeCouponCodeMutation,
  RemoveCouponCodeMutationVariables,
  removeItemFromOrderMutation,
  RemoveItemFromOrderMutationVariables,
} from "@/actions/mutations/order"
import { useCartDrawer } from "@/hooks/useCartDrawer"

export function useOrder() {
  const { toast } = useToast()
  const t = useScopedI18n("order")
  const { openCartDrawer } = useCartDrawer()

  const addItemToOrder = useMutation({
    mutationFn: async (variables: AddItemToOrderMutationVariables) =>
      addItemToOrderMutation(variables),
    onSuccess: (data) => {
      if (data.__typename === "Order") {
        openCartDrawer()
      } else if (data.__typename === "InsufficientStockError") {
        toast({
          variant: "destructive",
          title: t("addItem.error.insufficientStock.title"),
          description: t("addItem.error.insufficientStock.description"),
        })
      } else {
        toast({
          variant: "destructive",
          title: t("addItem.error.title"),
          description: data.message || t("addItem.error.description"),
        })
      }
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("addItem.error.title"),
        description: t("addItem.error.unexpectedDescription"),
      })
    },
  })

  const removeItemFromOrder = useMutation({
    mutationFn: (variables: RemoveItemFromOrderMutationVariables) =>
      removeItemFromOrderMutation(variables),
    onSuccess: (data) => {
      if (data.__typename !== "Order") {
        toast({
          variant: "destructive",
          title: t("removeItem.error.title"),
          description: t("removeItem.error.description"),
        })
      }
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("removeItem.error.title"),
        description: t("removeItem.error.unexpectedDescription"),
      })
    },
  })

  const adjustItemQuantity = useMutation({
    mutationFn: (variables: AdjustItemQuantityMutationVariables) =>
      adjustItemQuantityMutation(variables),
    onSuccess: (data) => {
      if (data.__typename !== "Order") {
        toast({
          variant: "destructive",
          title: t("adjustQuantity.error.title"),
          description: t("adjustQuantity.error.description"),
        })
      }
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("adjustQuantity.error.title"),
        description: t("adjustQuantity.error.unexpectedDescription"),
      })
    },
  })

  const applyCouponCode = useMutation({
    mutationFn: (variables: ApplyCouponCodeMutationVariables) =>
      applyCouponCodeMutation(variables),
  })

  const removeCouponCode = useMutation({
    mutationFn: (variables: RemoveCouponCodeMutationVariables) =>
      removeCouponCodeMutation(variables),
  })

  return {
    addItemToOrder,
    removeItemFromOrder,
    adjustItemQuantity,
    applyCouponCode,
    removeCouponCode,
  }
}
